import React from 'react';
import { PermitLetter, Employee, Branch } from '../../types';
import { PageHeader } from '../../components/Common';
import { TaeenFormState } from './templates';

interface GeneratorFormProps {
  form: TaeenFormState;
  setForm: React.Dispatch<React.SetStateAction<TaeenFormState>>;
  selectedLetter: PermitLetter;
  vessels: Branch[];
  employees: Employee[];
  previewHtml: string;
  onGeneratePreview: () => void;
  onPrintLetter: () => void;
  onClose: () => void;
}

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

const TaeenGeneratorForm: React.FC<GeneratorFormProps> = ({
  form, setForm, selectedLetter, vessels, employees, previewHtml, onGeneratePreview, onPrintLetter, onClose,
}) => {
  const toggleEmployee = (empId: string) => {
    setForm((prev) => ({
      ...prev,
      selectedEmployeeIds: prev.selectedEmployeeIds.includes(empId)
        ? prev.selectedEmployeeIds.filter((id) => id !== empId)
        : [...prev.selectedEmployeeIds, empId],
    }));
  };

  const selectAllEmployees = () => {
    setForm((prev) => ({
      ...prev,
      selectedEmployeeIds: prev.selectedEmployeeIds.length === employees.length ? [] : employees.map((e) => e.id),
    }));
  };

  return (
    <div className="space-y-6">
      <PageHeader title="طلب تعيين بحارة - Immigration Sign On" description={selectedLetter.templateName}
        actions={[{ label: 'Back', onClick: onClose, variant: 'info' }]}
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6 space-y-5">
          <h2 className="text-lg font-semibold text-gray-900 border-b pb-3">بيانات الخطاب</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">مكتب الجوازات (Passport Office)</label>
            <input type="text" placeholder="مثال: بورسعيد" value={form.passportOffice} onChange={(e) => setForm((p) => ({ ...p, passportOffice: e.target.value }))} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">الوحدة (Vessel)</label>
            <select value={form.vesselId} onChange={(e) => setForm((p) => ({ ...p, vesselId: e.target.value }))} className={inputClass}>
              <option value="">-- اختر الوحدة --</option>
              {vessels.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">العلم (Flag)</label>
              <input type="text" value={form.flag} onChange={(e) => setForm((p) => ({ ...p, flag: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">جنسية المالك (Owner Nationality)</label>
              <input type="text" value={form.ownerNationality} onChange={(e) => setForm((p) => ({ ...p, ownerNationality: e.target.value }))} className={inputClass} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ميناء القدوم (From Port)</label>
              <input type="text" value={form.fromPort} onChange={(e) => setForm((p) => ({ ...p, fromPort: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">المتواجدة حاليا (Current Location)</label>
              <input type="text" value={form.currentLocation} onChange={(e) => setForm((p) => ({ ...p, currentLocation: e.target.value }))} className={inputClass} />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">اختيار البحارة (Select Employees)</label>
              <button type="button" onClick={selectAllEmployees} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                {form.selectedEmployeeIds.length === employees.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="border border-gray-300 rounded-md max-h-60 overflow-y-auto divide-y divide-gray-100">
              {employees.length === 0 && <div className="p-4 text-center text-sm text-gray-500">No employees found</div>}
              {employees.map((emp) => (
                <label key={emp.id} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                  <input type="checkbox" checked={form.selectedEmployeeIds.includes(emp.id)} onChange={() => toggleEmployee(emp.id)} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                  <span className="ml-3 text-sm text-gray-800">{emp.name} — {emp.position || emp.rank || ''}</span>
                </label>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500">{form.selectedEmployeeIds.length} employee(s) selected</p>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onGeneratePreview} disabled={!form.passportOffice || !form.vesselId || form.selectedEmployeeIds.length === 0} className="flex-1 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Generate</button>
            {previewHtml && <button type="button" onClick={onPrintLetter} className="flex-1 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors">Print</button>}
          </div>
        </div>
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 border-b pb-3 mb-4">معاينة الخطاب (Preview)</h2>
          {previewHtml ? (
            <div className="border border-gray-200 rounded-md bg-white p-2 overflow-auto" style={{ minHeight: 400 }}>
              <iframe srcDoc={previewHtml} title="Letter Preview" className="w-full border-0" style={{ minHeight: 600 }} />
            </div>
          ) : (
            <div className="flex items-center justify-center text-gray-400 border border-dashed border-gray-300 rounded-md" style={{ minHeight: 400 }}>
              <div className="text-center">
                <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
                <p className="mt-2 text-sm">Fill in the form and click Generate to preview the letter</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaeenGeneratorForm;
